import { getTimeHealth, isIdle, computeSlaRisk, SLA_RANK } from '../store/useOperationalStore';

/**
 * Customer relationship types — ordered from highest to lowest priority.
 * SOLD   = unit sold by the operating organization; higher relationship weight
 * REGULAR = local repeat customer
 * FLEET  = fleet account
 * WALKIN = first-time / random breakdown
 */
export const CUSTOMER_TYPES = [
  { value: 'SOLD',    label: 'Sold Truck',        color: '#a78bfa' },
  { value: 'REGULAR', label: 'Regular Customer',  color: '#22c55e' },
  { value: 'FLEET',   label: 'Fleet Account',     color: '#f59e0b' },
  { value: 'WALKIN',  label: 'Walk-in',           color: '#64748b' },
];

/** Base score by customer type — sets the priority ring. */
const TYPE_BASE = {
  SOLD:    65,
  REGULAR: 60,
  FLEET:   20,
  WALKIN:  0,
};

/**
 * Computes a 0–100 priority score for a truck. This is a SECONDARY,
 * fine-grained sort key used to order trucks within the same urgency tier
 * (see `urgencyTier` below, which is the authoritative signal for color and
 * ring placement). Score alone must never decide color — it's derived from
 * optional fields (customerType, promisedTime) that aren't always set.
 *
 * Primary driver: customer relationship (who they are to the shop)
 * Secondary: fleet-down status (boosts within tier)
 * Tertiary: small urgency nudges (promise time, time health, idle, overall age)
 */
export function computePriorityScore(truck) {
  if (!truck || ['completed', 'voided'].includes(truck.status)) return 0;

  const base = TYPE_BASE[truck.customerType] ?? 0;
  let score = base;

  // Fleet down: major boost within tier
  if (truck.isHardDown) score += 20;

  // Promise time urgency (small nudge)
  if (truck.promisedTime) {
    const now       = Date.now();
    const remaining = truck.promisedTime - now;
    const totalSpan = truck.promisedTime - (truck.startTime ? truck.startTime * 1000 : now);
    if (remaining < 0) {
      score += 5;
    } else if (totalSpan > 0) {
      const pctLeft = remaining / totalSpan;
      if (pctLeft < 0.10) score += 4;
      else if (pctLeft < 0.25) score += 2;
    }
  }

  // Time health nudge
  const health = getTimeHealth(truck);
  if (health?.status === 'red') score += 3;

  // Idle nudge
  if (isIdle(truck)) score += 2;

  // Overall age nudge — so jobs with no estimate/promise still climb the
  // sort order the longer they sit open, instead of staying pinned low.
  if (truck.startTime) {
    const ageDays = (Date.now() - truck.startTime * 1000) / 86_400_000;
    score += Math.min(15, ageDays); // up to +15 for very old ROs
  }

  return Math.min(100, Math.round(score));
}

/**
 * Authoritative urgency tier for a truck — drives both ring placement and
 * node color in PriorityView. Backed by computeSlaRisk, which escalates on
 * hard-down, promise breach, time health, stage age, AND overall age, so a
 * truck open for months with no estimate ever set still escalates instead
 * of reading permanently green.
 *
 * Returns a ring index: 0 = CRITICAL (innermost), 1 = HIGH, 2 = MEDIUM, 3 = LOW.
 */
export function urgencyTier(truck) {
  const risk = computeSlaRisk(truck);
  return 3 - (SLA_RANK[risk] ?? 0);
}

/** @deprecated use urgencyTier(truck) — kept for any lingering callers. */
export function scoreToRing(score) {
  if (score >= 80) return 0; // CRITICAL
  if (score >= 55) return 1; // HIGH
  if (score >= 30) return 2; // MEDIUM
  return 3;                  // LOW
}

/** Returns the display label and color for a customerType value. */
export function customerTypeInfo(type) {
  return CUSTOMER_TYPES.find(t => t.value === type) ?? { label: 'Unknown', color: '#475569' };
}
